/** @jsx jsx */
import { useEffect, useRef } from 'react';
import {
  compact,
  filter,
  indexOf,
  map,
  pipe,
  prop,
  sortBy,
  toPairs,
} from 'lodash/fp';
import { jsx } from '@emotion/core';
import { Alert } from '@zapier/design-system';
import { scrollTo } from '@zapier/payment/src/utils/scrollTo';
import { FieldError } from '@zapier/payment/src/types';

const FIELDS_ORDER = [
  'number',
  'name',
  'expiry',
  'cvc',
  'addressCountry',
  'addressState',
  'addressZip',
];

const toErrorsArray = pipe([
  toPairs,
  sortBy(([fieldName]) => indexOf(fieldName, FIELDS_ORDER)),
  map('1'),
  filter(Boolean),
]);

const countFixedErrors = (errors: FieldError[]): number =>
  pipe([map('isFixed'), compact, prop('length')])(errors);

const getMessageBarKind = (errors: FieldError[], fixedErrorsCount: number) =>
  fixedErrorsCount === errors.length ? 'success' : 'error';

type Props = {
  /** Array of Field Errors to show */
  errors: FieldError[];
};

const textCss = (isFixed: boolean = false) => ({
  fontWeight: isFixed ? 400 : 600,
  textDecoration: isFixed ? 'line-through' : 'none',
});

const renderError = (error: FieldError) => (
  <li css={textCss(error.isFixed)} key={error.message}>
    {error.message}
  </li>
);

const CreditCardErrors: React.FC<Props> = props => {
  const rootRef = useRef<HTMLDivElement>(null);
  const errors = toErrorsArray(props.errors);
  const fixedErrorsCount = countFixedErrors(errors);
  const title =
    fixedErrorsCount < errors.length
      ? "We're almost there! The following items need your attention:"
      : 'Everything looks good now!';

  useEffect(() => {
    scrollTo(rootRef.current); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={rootRef}>
      <Alert title={title} type={getMessageBarKind(errors, fixedErrorsCount)}>
        {Array.isArray(errors) && errors.length > 0 && (
          <ul>{map(renderError, errors)}</ul>
        )}
      </Alert>
    </div>
  );
};

export default CreditCardErrors;
